/**
 * 대시보드 셸: 헤더 / [좌 사이드바 | 중앙 그래프 | 우 상세] / 하단 이벤트 로그.
 * - 마운트 시 PipelineSimulator 기동, 언마운트 시 정지 (서버/DB 호출 없음 — 데모 전용).
 * - 단축키: Esc = 선택 해제(상세 닫기), Space = 일시정지/재생 (입력 요소 포커스 시 제외).
 * - 반응형: ≥1440 3패널, 1024~1439 좌 패널 접기 가능, 768~1023 우 상세 오버레이, <768 양쪽 드로어.
 */
import { AlertTriangle, RefreshCw, Workflow, X } from "lucide-react";
import { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { PipelineSimulator } from "../../lib/pipeline-simulator";
import { selectActions, selectError, selectFeedStatus, selectMode, selectNodes, selectSelectedNodeId, selectUiState, usePipelineStore, } from "../../store/pipeline-store";
import { DashboardHeader } from "./DashboardHeader";
import { EventLogPanel } from "./EventLogPanel";
import { NodeDetailPanel } from "./NodeDetailPanel";
import { SourceSidebar } from "./SourceSidebar";
import { useBreakpoint } from "./hooks";
import { IconButton, Skeleton, cn } from "./ui";
const SIDEBAR_W = 280;
const DETAIL_W = 360;
const PipelineCanvasLazy = lazy(() => import("../flow/PipelineCanvas"));
/** React Flow 번들은 무겁다 → 지연 로드 (Next 의 dynamic(ssr:false) 대체) */
function PipelineCanvas(props) {
    return (<Suspense fallback={<CanvasSkeleton label="그래프 모듈 불러오는 중…"/>}>
      <PipelineCanvasLazy {...props}/>
    </Suspense>);
}
function CanvasSkeleton({ label }) {
    return (<div className="relative h-full w-full overflow-hidden" aria-busy="true" aria-label={label}>
      <div className="absolute inset-0 grid grid-cols-[1fr_1fr_1fr_1fr] gap-6 p-6 opacity-70">
        <div className="space-y-3">
          {Array.from({ length: 8 }, (_, i) => (<Skeleton key={i} className="h-12 w-full"/>))}
        </div>
        <div className="flex flex-col justify-center space-y-6">
          {Array.from({ length: 3 }, (_, i) => (<Skeleton key={i} className="h-14 w-full"/>))}
        </div>
        <div className="flex flex-col justify-center space-y-4">
          {Array.from({ length: 5 }, (_, i) => (<Skeleton key={i} className="h-12 w-full"/>))}
        </div>
        <div className="flex flex-col justify-center space-y-6">
          {Array.from({ length: 2 }, (_, i) => (<Skeleton key={i} className="h-14 w-full"/>))}
        </div>
      </div>
      <div className="absolute inset-x-0 bottom-6 text-center text-xs text-fg-subtle">{label}</div>
    </div>);
}
function EmptyState({ onRetry }) {
    return (<div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
      <Workflow aria-hidden size={40} className="text-fg-subtle"/>
      <div className="text-sm font-semibold">표시할 파이프라인 노드가 없습니다</div>
      <p className="max-w-sm text-xs text-fg-muted">
        스냅샷에 노드가 포함되어 있지 않습니다. DB 연결 상태를 확인하거나 다시 시도하세요.
      </p>
      <button type="button" onClick={onRetry} className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs hover:bg-surface-hover">
        <RefreshCw size={12} aria-hidden/> 다시 시도
      </button>
    </div>);
}
function ErrorBanner({ message, onRetry, onDismiss }) {
    return (<div role="alert" className="pointer-events-auto flex items-center gap-2 rounded-md border border-status-error/50 bg-bg-elevated/95 px-3 py-2 text-xs shadow-node backdrop-blur">
      <AlertTriangle aria-hidden size={14} className="shrink-0 text-status-error"/>
      <span className="min-w-0 flex-1 truncate" title={message}>
        {message}
      </span>
      <button type="button" onClick={onRetry} title="스냅샷 다시 요청" className="inline-flex shrink-0 items-center gap-1 rounded border border-border px-2 py-1 hover:bg-surface-hover">
        <RefreshCw size={12} aria-hidden/> 재시도
      </button>
      <IconButton label="배너 닫기" onClick={onDismiss} className="size-6 border-0">
        <X size={12}/>
      </IconButton>
    </div>);
}
function isTypingTarget(t) {
    if (!(t instanceof HTMLElement))
        return false;
    const tag = t.tagName;
    return (tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        tag === "BUTTON" ||
        t.isContentEditable ||
        t.getAttribute("role") === "textbox");
}
export function DataFlowDashboard() {
    // 브레이크포인트는 뷰포트가 아니라 이 컨테이너 너비로 판정한다 (SMI 사이드바 폭 반영)
    const rootRef = useRef(null);
    const bp = useBreakpoint(rootRef);
    const nodes = usePipelineStore(selectNodes);
    const mode = usePipelineStore(selectMode);
    const feedStatus = usePipelineStore(selectFeedStatus);
    const error = usePipelineStore(selectError);
    const selectedId = usePipelineStore(selectSelectedNodeId);
    const ui = usePipelineStore(selectUiState);
    const { selectNode, togglePaused, setUiState } = usePipelineStore(selectActions);
    const simRef = useRef(null);
    const [bannerDismissedFor, setBannerDismissedFor] = useState(null);
    // 시뮬레이터 시작/정지.
    // 원본은 /api/pipeline/snapshot 을 2초 폴링했지만, 이 화면은 데모 전용이라
    // 서버·DB 없이 브라우저 안에서만 스냅샷을 만든다 (fetch 호출 0).
    useEffect(() => {
        const { applySnapshot } = usePipelineStore.getState().actions;
        const sim = new PipelineSimulator({ mode: "mock" });
        simRef.current = sim;
        const unsubscribe = sim.subscribe((snap) => {
            if (!usePipelineStore.getState().paused)
                applySnapshot(snap);
        });
        applySnapshot(sim.getSnapshot()); // 첫 화면 즉시 채움
        sim.start();
        // 헤더의 일시정지 버튼(store.paused)을 시뮬레이터에 전파
        const unsubPaused = usePipelineStore.subscribe((st, prev) => {
            if (st.paused !== prev.paused)
                sim.setPaused(st.paused);
        });
        return () => {
            sim.stop();
            unsubscribe();
            unsubPaused();
            simRef.current = null;
        };
    }, []);
    const overlayDetail = bp === "mobile" || bp === "tablet";
    const drawerSidebar = bp === "mobile";
    /** 1024~1439: 상세 패널은 인라인이지만 기본 접힘, 노드 선택 시 자동 펼침 */
    const autoOpenDetail = overlayDetail || bp === "desktop";
    // 단축키
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") {
                selectNode(null);
                // 오버레이·드로어 모드에서는 열린 패널도 닫는다
                const patch = {};
                if (overlayDetail)
                    patch.detailCollapsed = true;
                if (drawerSidebar)
                    patch.sidebarCollapsed = true;
                if (Object.keys(patch).length)
                    setUiState(patch);
                return;
            }
            if ((e.key === " " || e.code === "Space") && !isTypingTarget(e.target)) {
                e.preventDefault();
                togglePaused();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [selectNode, togglePaused, setUiState, overlayDetail, drawerSidebar]);
    // 브레이크포인트 전환 시 패널 기본 상태
    const prevBp = useRef(null);
    useEffect(() => {
        if (prevBp.current === bp)
            return;
        prevBp.current = bp;
        if (bp === "mobile")
            setUiState({ sidebarCollapsed: true, detailCollapsed: true });
        else if (bp === "tablet")
            setUiState({ sidebarCollapsed: false, detailCollapsed: true });
        else if (bp === "desktop")
            setUiState({ sidebarCollapsed: false, detailCollapsed: true });
        else
            setUiState({ sidebarCollapsed: false, detailCollapsed: false });
    }, [bp, setUiState]);
    // 오버레이·데스크톱 모드: 노드 선택 시 상세 패널 자동 열기
    useEffect(() => {
        if (autoOpenDetail && selectedId)
            setUiState({ detailCollapsed: false });
    }, [autoOpenDetail, selectedId, setUiState]);
    const closeDetail = useCallback(() => {
        selectNode(null);
        if (autoOpenDetail)
            setUiState({ detailCollapsed: true });
    }, [autoOpenDetail, selectNode, setUiState]);
    const closeSidebarIfDrawer = useCallback(() => {
        if (drawerSidebar)
            setUiState({ sidebarCollapsed: true });
    }, [drawerSidebar, setUiState]);
    const retry = useCallback(() => {
        setBannerDismissedFor(null);
        simRef.current?.step();
    }, []);
    const sidebarOpen = !ui.sidebarCollapsed;
    const detailOpen = !ui.detailCollapsed;
    const loading = mode === "idle" && nodes.length === 0 && feedStatus !== "error";
    const empty = mode !== "idle" && nodes.length === 0;
    const showError = !!error && (feedStatus === "error" || mode === "mock-fallback") && bannerDismissedFor !== error;
    const errorMessage = feedStatus === "error" ? `API 오류: ${error}` : `DB 연결 실패 — mock 시뮬레이터로 표시 중 (${error}). 30초마다 자동 재시도합니다.`;
    return (<div ref={rootRef} className="grid h-full min-w-0 grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden bg-bg text-fg">
      <DashboardHeader breakpoint={bp} sidebarOpen={sidebarOpen} detailOpen={detailOpen} onToggleSidebar={() => setUiState({ sidebarCollapsed: !ui.sidebarCollapsed })} onToggleDetail={() => setUiState({ detailCollapsed: !ui.detailCollapsed })}/>

      <div className="relative flex min-h-0 min-w-0">
        {/* 좌 사이드바: 데스크톱 = 인라인(접힘 시 폭 0), 모바일 = 드로어 */}
        {drawerSidebar ? (<>
            <div aria-hidden={!sidebarOpen} onClick={() => setUiState({ sidebarCollapsed: true })} className={cn("absolute inset-0 z-20 bg-black/50 transition-opacity duration-200", sidebarOpen ? "opacity-100" : "pointer-events-none opacity-0")}/>
            <div className={cn("absolute inset-y-0 left-0 z-30 w-[min(85vw,300px)] border-r border-border shadow-node transition-transform duration-200", sidebarOpen ? "translate-x-0" : "-translate-x-full")} aria-hidden={!sidebarOpen}>
              <SourceSidebar onNavigate={closeSidebarIfDrawer}/>
            </div>
          </>) : (<div className={cn("shrink-0 overflow-hidden border-r border-border transition-[width] duration-200", !sidebarOpen && "border-r-0")} style={{ width: sidebarOpen ? SIDEBAR_W : 0 }} aria-hidden={!sidebarOpen}>
            <div style={{ width: SIDEBAR_W }} className="h-full">
              <SourceSidebar />
            </div>
          </div>)}

        {/* 중앙 그래프 */}
        <main className="relative min-h-0 min-w-0 flex-1 overflow-hidden" aria-label="파이프라인 그래프">
          {loading ? (<CanvasSkeleton label="첫 스냅샷 대기 중…"/>) : empty ? (<EmptyState onRetry={retry}/>) : (<PipelineCanvas />)}
          {showError && (<div className="pointer-events-none absolute inset-x-3 top-3 z-10 flex justify-center">
              <div className="w-full max-w-2xl">
                <ErrorBanner message={errorMessage} onRetry={retry} onDismiss={() => setBannerDismissedFor(error)}/>
              </div>
            </div>)}
        </main>

        {/* 우 상세: 데스크톱 = 인라인, 태블릿/모바일 = 오버레이 드로어 */}
        {overlayDetail ? (<>
            <div aria-hidden={!detailOpen} onClick={() => setUiState({ detailCollapsed: true })} className={cn("absolute inset-0 z-20 bg-black/40 transition-opacity duration-200", detailOpen ? "opacity-100" : "pointer-events-none opacity-0")}/>
            <div className={cn("absolute inset-y-0 right-0 z-30 w-[min(92vw,380px)] border-l border-border shadow-node transition-transform duration-200", detailOpen ? "translate-x-0" : "translate-x-full")} aria-hidden={!detailOpen}>
              <NodeDetailPanel onClose={closeDetail}/>
            </div>
          </>) : (<div className={cn("shrink-0 overflow-hidden border-l border-border transition-[width] duration-200", !detailOpen && "border-l-0")} style={{ width: detailOpen ? DETAIL_W : 0 }} aria-hidden={!detailOpen}>
            <div style={{ width: DETAIL_W }} className="h-full">
              <NodeDetailPanel onClose={closeDetail}/>
            </div>
          </div>)}
      </div>

      <EventLogPanel onNavigate={closeSidebarIfDrawer}/>
    </div>);
}
export default DataFlowDashboard;
